import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * GIAO DIỆN SÁNG / TỐI.
 *
 * • 3 lựa chọn: 'light' · 'dark' · 'system' (bám theo cài đặt máy người dùng).
 * • Lựa chọn được lưu localStorage và áp lại NGAY trong <head> (xem index.html)
 *   nên không bị chớp màu khi tải trang.
 * • Bảng màu của từng chế độ nằm ở `src/styles/globals.css` — component chỉ
 *   dùng token (bg-surface, text-text-muted…), KHÔNG viết mã màu cứng.
 */

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'upbo-theme'

interface ThemeValue {
  /** Lựa chọn của người dùng (có thể là 'system') */
  mode: ThemeMode
  /** Chế độ thực sự đang hiển thị */
  theme: ResolvedTheme
  setMode: (mode: ThemeMode) => void
  /** Đổi nhanh sáng ↔ tối (nút một chạm trên mobile) */
  toggle: () => void
}

const ThemeContext = createContext<ThemeValue | null>(null)

function prefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

function readStoredMode(fallback: ThemeMode): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved
  } catch {
    /* storage bị chặn */
  }
  return fallback
}

export function ThemeProvider({ children, defaultMode = 'system' }: { children: ReactNode; defaultMode?: ThemeMode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode(defaultMode))
  const [systemDark, setSystemDark] = useState<boolean>(() => prefersDark())

  // Theo dõi cài đặt hệ thống để chế độ 'system' đổi theo ngay lập tức.
  useEffect(() => {
    let mq: MediaQueryList
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)')
    } catch {
      return
    }
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const theme: ResolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.style.colorScheme = theme
  }, [theme])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* bỏ qua */
    }
  }, [])

  const value = useMemo<ThemeValue>(
    () => ({ mode, theme, setMode, toggle: () => setMode(theme === 'dark' ? 'light' : 'dark') }),
    [mode, theme, setMode],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme phải nằm trong <ThemeProvider>')
  return ctx
}
